<template>
  <v-sheet class="pa-4 h-100">
    <v-sheet outlined class="rounded overflow-hidden h-100 d-flex flex-column">
      <div
        class="pa-2 d-flex align-center justify-space-between"
        style="gap: 8px"
        :class="{
          'flex-wrap': $vuetify.breakpoint.xs,
        }"
      >
        <h3>Manage Groups</h3>
        <v-btn 
          v-has="permissions.Facilities.Create" 
          @click="openAddDialog()" 
          small 
          color="primary"
        >
          <v-icon left>mdi-plus</v-icon> Add Group
        </v-btn>
        <v-spacer v-if="!$vuetify.breakpoint.xs"></v-spacer>
        <v-sheet :width="$vuetify.breakpoint.xs ? '100%' : 250">
          <v-text-field
            hide-details
            outlined
            v-model="options.search"
            dense
            class="search-bar"
            placeholder="Search in groups..."
            clearable
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </v-sheet>
      </div>
      
      <v-data-table
        class="elevation-0 pa-2 rounded-0 d-flex flex-column table-sticky custom-table"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-chevron-double-left',
          lastIcon: 'mdi-chevron-double-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
          itemsPerPageText: 'Row/Page',
          itemsPerPageOptions: [15, 30, 50, 100, 200, 300, 400, 500, -1],
        }"
        :headers="groupsHeaders"
        :items="entries"
        :options.sync="options"
        @click:row="openEditDialog"
        :loading="loading"
        :server-items-length="total"
      >
        <template v-slot:[`item.name`]="{ item }">
          <h4>{{ item.name }}</h4>
        </template>

        <template v-slot:[`item.description`]="{ item }">
          <span>{{ item.description || 'No description' }}</span>
        </template>

        <template v-slot:[`item.facilitiesCount`]="{ item }">
          <v-chip small color="primary" outlined>
            {{ item.facilitiesCount || 0 }}
          </v-chip>
        </template>

        <template v-slot:[`item.createdBy`]="{ item }">
          <span>{{ item.createdBy || 'System' }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-center" style="gap: 8px">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-has="permissions.Facilities.Update"
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openEditDialog(item)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit Group</span>
            </v-tooltip>
            
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-has="permissions.Facilities.Delete"
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="confirmDelete(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Group</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">No Data Available!</p>
          </div>
        </template>

        <template v-slot:loading>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">Loading Groups...</p>
          </div>
        </template>
      </v-data-table>
    </v-sheet>

    <!-- Add/Edit Dialog -->
    <add-edit-group
      v-model="showDialog"
      :edit-group="selectedGroup"
      @close="closeDialog"
      @saved="onGroupSaved"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete the group "{{ groupToDelete && groupToDelete.name }}"?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="showDeleteDialog = false">
            Cancel
          </v-btn>
          <v-btn 
            color="error" 
            text 
            @click="deleteGroup"
            :loading="deleteLoading"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { groupsHeaders } from '@Groups/config/groups.headers';
import AddEditGroup from '@Groups/components/AddEditGroup.vue';
import permissions from '@Share/config/permissions';
import { error } from '@/utils/debugLogger';

export default {
  name: 'GroupsList',
  components: {
    AddEditGroup,
  },
  data() {
    return {
      permissions,
      groupsHeaders,
      showDialog: false,
      showDeleteDialog: false,
      selectedGroup: null,
      groupToDelete: null,
      deleteLoading: false,
    };
  },
  computed: {
    ...mapGetters('GROUPS', {
      entries: 'GET_ENTRIES',
      total: 'GET_TOTAL',
      loading: 'GET_LOADING',
    }),
    options: {
      get() {
        return this.$store.getters['GROUPS/GET_OPTIONS'];
      },
      set(val) {
        this.$store.commit('GROUPS/SET_OPTIONS', val);
      },
    },
  },
  watch: {
    options: {
      handler() {
        this.loadGroups();
      },
      deep: true,
    },
  },
  mounted() {
    this.loadGroups();
  },
  methods: {
    ...mapActions('GROUPS', ['GET', 'DELETE']),
    
    loadGroups() {
      this.GET();
    },
    
    openAddDialog() {
      this.selectedGroup = null;
      this.showDialog = true;
    },
    
    openEditDialog(group) {
      this.selectedGroup = { ...group };
      this.showDialog = true;
    },
    
    closeDialog() {
      this.selectedGroup = null;
      this.showDialog = false;
    },
    
    onGroupSaved() {
      this.loadGroups();
    },
    
    confirmDelete(group) {
      this.groupToDelete = group;
      this.showDeleteDialog = true;
    },
    
    async deleteGroup() {
      if (!this.groupToDelete) return;
      
      this.deleteLoading = true;
      try {
        await this.DELETE(this.groupToDelete.id);
        this.showDeleteDialog = false;
        this.groupToDelete = null;
        this.loadGroups();
      } catch (err) {
        error('GroupsList', 'Error deleting group', err);
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.custom-table {
  flex: 1 1 auto;
}

.table-sticky {
  position: relative;
}

.search-bar {
  max-width: 300px;
}
</style>
